import React, { useState, useEffect } from 'react';
import './CategoryPage.css';
import PriceComparison from '../components/PriceComparison';
import ProductCardGrid from '../components/ProductCardGrid';
import { searchProducts, getStores } from '../utils/db';

function CategoryPage({ category, onAddToBasket, onBack }) {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [sortBy, setSortBy] = useState('name-asc');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const allProducts = await searchProducts('');
        const storesData = await getStores();

        const categoryProducts = allProducts.filter(
          p => p.category?.toLowerCase() === category.id.toLowerCase()
        );

        setProducts(categoryProducts);
        setStores(storesData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [category]);

  const handleTierToggle = (tier) => {
    setSelectedTiers(prev =>
      prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]
    );
  };

  const getCheapestPrice = (product) => {
    if (!product.prices?.length) return null;
    const valid = product.prices.filter(p => p.price > 0);
    if (!valid.length) return null;
    return Math.min(...valid.map(p => p.price));
  };

  const getFilteredProducts = () => {
    let filtered = [...products];

    if (selectedTiers.length > 0) {
      filtered = filtered.filter(
        p => p.ownBrand === true && selectedTiers.includes(p.tier)
      );
    }

    filtered.sort((a, b) => {
      if (sortBy === 'name-asc')  return a.name.localeCompare(b.name);
      if (sortBy === 'name-desc') return b.name.localeCompare(a.name);
      if (sortBy === 'price-asc') {
        return (getCheapestPrice(a) ?? Infinity) - (getCheapestPrice(b) ?? Infinity);
      }
      return 0;
    });

    return filtered;
  };

  // Full price comparison screen
  if (selectedProduct) {
    // Use the product's own prices directly (same approach as ProductCardGrid)
    // so storeId format differences don't cause prices to silently resolve to 0
    const prices = (selectedProduct.prices || [])
      .filter(p => p.price > 0)
      .map(p => {
        const store = stores.find(s => s.id === p.storeId);
        const name  = store?.name || p.storeId.replace(/^store_/, '').replace(/_/g, ' ');
        // Spread all price fields so size/standardizedSize/unitType reach PriceComparison
        return { ...p, storeName: name };
      });

    return (
      <div className="category-page">
        <PriceComparison
          product={selectedProduct}
          prices={prices}
          stores={stores}
          onBack={() => setSelectedProduct(null)}
          onAddToBasket={onAddToBasket}
        />
      </div>
    );
  }

  const filteredProducts = getFilteredProducts();
  const sortLabel = sortBy === 'name-asc' ? 'A–Z' : sortBy === 'name-desc' ? 'Z–A' : 'lowest price first';

  return (
    <div className="category-page">

      {/* ── Breadcrumb / header bar ── */}
      <div className="cat-breadcrumb">
        <button onClick={onBack} className="cat-back-btn" aria-label="Back to Browse">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="13 18 7 12 13 6"/>
          </svg>
          <span>Browse</span>
        </button>
        <span className="cat-header-icon" aria-hidden="true">{category.icon}</span>
        <span className="cat-title">{category.name}</span>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="cat-sort-select"
          aria-label="Sort products"
        >
          <option value="name-asc">A–Z</option>
          <option value="name-desc">Z–A</option>
          <option value="price-asc">Price ↑</option>
        </select>
      </div>

      {/* ── Screen orientation hint ── */}
      <p className="screen-hint">Products in this category across stores. Tap any item to compare prices.</p>

      {/* ── Tier filter pills ── */}
      <div className="cat-filter-row">
        {['basic', 'standard', 'premium'].map(tier => (
          <button
            key={tier}
            className={`cat-filter-btn${selectedTiers.includes(tier) ? ' active' : ''}`}
            onClick={() => handleTierToggle(tier)}
          >
            {tier.charAt(0).toUpperCase() + tier.slice(1)}
          </button>
        ))}
        {selectedTiers.length > 0 && (
          <button className="cat-clear-btn" onClick={() => setSelectedTiers([])}>Clear</button>
        )}
      </div>

      {/* ── Count + sort label ── */}
      {!loading && (
        <div className="cat-meta-row">
          <span className="cat-count">{filteredProducts.length} products</span>
          <span className="cat-sort-label">sorted {sortLabel}</span>
        </div>
      )}

      {/* ── Card grid ── */}
      {loading ? (
        <div className="cat-loading">Loading products…</div>
      ) : filteredProducts.length === 0 ? (
        <div className="cat-empty">
          <p>No products found.</p>
          {selectedTiers.length > 0 && (
            <button className="cat-filter-btn" onClick={() => setSelectedTiers([])}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <ProductCardGrid
          products={filteredProducts}
          stores={stores}
          onSelectProduct={setSelectedProduct}
          onAddToBasket={onAddToBasket}
        />
      )}

    </div>
  );
}

export default CategoryPage;
